import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { AccountError } from '../account/api.ts';
import { READING_ROLES, members, openPreview, sharedRealms, updateCopies, type ReadingRole } from '../account/sharing.ts';
import { RealmClientError } from '../db/client.ts';
import { realmKey, type SharedCopy } from '../db/protocol.ts';
import { copyName } from './RealmRole.tsx';

function message(error: unknown): string {
  return error instanceof AccountError || error instanceof RealmClientError ? error.message : 'The preview couldn’t be made. Please try again.';
}

/**
 * Preview as Player and Preview as Reader (docs/SHARING-CO-OWNERS-PLAN.md section 2): the
 * copy each would get, made now from this account's realm (`file`) and opened read-only.
 */
export function PreviewButtons({ file }: { file: string }) {
  const navigate = useNavigate();
  const [making, setMaking] = useState<ReadingRole | null>(null);
  const [problem, setProblem] = useState('');

  async function preview(role: ReadingRole) {
    setProblem('');
    setMaking(role);
    try {
      const opened = await openPreview(file, role);
      await navigate({ to: '/realm/$realmKey', params: { realmKey: realmKey(opened) } });
    } catch (error) {
      setProblem(message(error));
    } finally {
      setMaking(null);
    }
  }

  return (
    <>
      {READING_ROLES.map((role) => (
        <button
          key={role.value}
          type="button"
          className="quiet"
          disabled={making !== null}
          title={`See the copy ${role.label.toLowerCase()}s would get, without sending it`}
          onClick={() => void preview(role.value)}
        >
          {making === role.value ? 'Making the preview…' : `Preview as ${role.label}`}
        </button>
      ))}
      {problem && (
        <p className="notice notice-error" role="alert">
          {problem}
        </p>
      )}
    </>
  );
}

/** Over a preview: what it is, Close preview, and on a shared realm Update players' copy. */
export function PreviewBanner({ shared }: { shared: SharedCopy }) {
  const navigate = useNavigate();
  const source = realmKey(shared.previewOf!);
  const [people, setPeople] = useState(0);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  useEffect(() => {
    let live = true;
    sharedRealms().then(
      (counts) => live && setPeople(counts.get(source) ?? 0),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [source]);

  async function update() {
    setProblem('');
    setBusy(true);
    try {
      const found = await members(source);
      if ('notBackedUp' in found) throw new AccountError('not_backed_up', 'This realm isn’t backed up yet, so its copies can’t be sent.');
      await updateCopies({ file: shared.previewOf!, realmId: source, realmName: shared.name, people: found.members });
      await navigate({ to: '/realm/$realmKey/sharing', params: { realmKey: source }, search: { sent: true } });
    } catch (error) {
      setProblem(message(error));
      setBusy(false);
    }
  }

  return (
    <div className="shared-banner preview-banner" role="note">
      <p>
        <strong>Preview of the {copyName(shared.role)}.</strong> Nothing has been sent: this is what{' '}
        {shared.role === 'player' ? 'players' : 'readers'} would get if you updated their copy now.
      </p>
      <div className="actions">
        <button type="button" className="quiet" disabled={busy} onClick={() => void navigate({ to: '/realm/$realmKey', params: { realmKey: source } })}>
          Close preview
        </button>
        {people > 0 && (
          <button type="button" className="primary" disabled={busy} onClick={() => void update()}>
            {busy ? 'Working…' : 'Update players’ copy'}
          </button>
        )}
      </div>
      {problem && (
        <p className="notice notice-error" role="alert">
          {problem}
        </p>
      )}
    </div>
  );
}
